// Settings, Defaults, the increase threshold editor (spec C14; Yehuda, Oct 29): its words and the
// checks the server makes again (apps/server/src/increase-rule.ts parseIncreaseRuleInput).
import type { IncreaseRule } from '../auth/api';

export const increaseCopy = {
  title: 'Increase threshold',
  sub: 'Defaults',
  readOnly: 'Only the Manager and the Admin change the increase threshold.',
  percent: 'Increase at (%)',
  dollars: 'Minimum increase ($)',
  dollarsHint: '0 means any amount',
  percentError: 'Enter a percent from 1 to 100, like 15 or 12.5.',
  dollarsError: 'Enter a whole dollar amount like 200.',
  saved: 'Increase threshold saved.',
  somethingWrong: 'Something went wrong. Try again.',
} as const;

/** What the inputs hold: text, so a half-typed number is kept as typed. */
export type IncreaseDraft = { percent: string; dollars: string };

export const draftOf = (rule: IncreaseRule): IncreaseDraft => ({
  percent: String(rule.percent),
  dollars: String(rule.minDollars),
});

/** The percent from 1 to 100 with at most one decimal, or null. */
export function percentOf(text: string): number | null {
  const t = text.trim();
  if (!/^\d{1,3}(\.\d)?$/.test(t)) return null;
  const n = Number(t);
  return n >= 1 && n <= 100 ? n : null;
}

/** Whole dollars, 0 or more ("1,000" too), or null. */
export function dollarsOf(text: string): number | null {
  const t = text.trim().replace(/^\$/, '');
  if (!/^\d{1,3}(,\d{3})*$|^\d+$/.test(t)) return null;
  const n = Number(t.replaceAll(',', ''));
  return Number.isSafeInteger(n) ? n : null;
}

/** The rule the draft makes, or null while either field is not valid. */
export function ruleOf(d: IncreaseDraft): IncreaseRule | null {
  const percent = percentOf(d.percent);
  const minDollars = dollarsOf(d.dollars);
  return percent === null || minDollars === null ? null : { percent, minDollars };
}

/** Why a save did not go through. */
export function saveError(error: string): string {
  if (error === 'forbidden') return increaseCopy.readOnly;
  return increaseCopy.somethingWrong;
}
